import React from 'react';
import { Link } from 'react-router-dom';
import { Download, ExternalLink, FileText, Loader2, PenLine } from 'lucide-react';
import { formatDisplayMoney } from '../../utils/formatMoney';

const STATUS_STYLES = {
  draft: 'bg-gray-100 text-gray-700 border-gray-200',
  sent: 'bg-amber-50 text-amber-800 border-amber-200',
  overdue: 'bg-red-50 text-red-700 border-red-200',
  paid: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  cancelled: 'bg-gray-100 text-gray-500 border-gray-200',
};

/** Statuses the client API allows for PDF download. */
const CLIENT_DOWNLOADABLE = new Set(['sent', 'overdue', 'paid']);

/**
 * Lists invoices linked to a booking (platform booking documents).
 * Vendor: links to invoice detail. Client: download when sent/paid.
 */
const BookingLinkedInvoices = ({
  invoices = [],
  variant = 'vendor', // vendor | client
  onDownload,
  downloadingId = null,
  className = '',
  emptyMessage = 'No invoices linked to this booking yet.',
  createHref = null,
}) => {
  const list = Array.isArray(invoices) ? invoices : [];
  const showCreate = variant === 'vendor' && createHref;

  if (list.length === 0 && !showCreate) {
    return null;
  }

  const formatDate = (value) => {
    if (!value) return '—';
    const raw = typeof value === 'string' ? value.slice(0, 10) : value;
    try {
      return new Date(`${raw}T12:00:00`).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return String(raw);
    }
  };

  return (
    <section
      className={`rounded-lg border border-border bg-surface p-5 sm:p-6 shadow-sm ${className}`}
    >
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-base font-bold text-ink flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Invoices
          </h2>
          <p className="text-sm text-ink-secondary mt-0.5">
            Documents linked to this booking
          </p>
        </div>
        {showCreate ? (
          <Link
            to={createHref}
            className="inline-flex shrink-0 items-center justify-center gap-1.5 rounded-lg bg-ink px-3 py-2 text-xs font-medium text-white hover:bg-black"
          >
            <PenLine className="w-3.5 h-3.5" />
            Create invoice
          </Link>
        ) : null}
      </div>

      {list.length === 0 ? (
        <p className="text-sm text-ink-tertiary">{emptyMessage}</p>
      ) : (
      <ul className="space-y-2.5">
        {list.map((invoice) => {
          const status = String(invoice.status || 'draft').toLowerCase();
          const docType =
            (invoice.document_type || 'invoice') === 'receipt' ? 'Receipt' : 'Invoice';
          const money = formatDisplayMoney(
            invoice.total_amount,
            invoice.currency || 'USD'
          );
          const isDownloading = downloadingId === invoice.id;
          const canDownload =
            variant === 'client' && CLIENT_DOWNLOADABLE.has(status) && Boolean(onDownload);
          const showActionNeeded =
            variant === 'client' &&
            (invoice.is_pending ||
              (CLIENT_DOWNLOADABLE.has(status) &&
                status !== 'paid' &&
                !invoice.client_downloaded_at));

          return (
            <li
              key={invoice.id}
              className="flex flex-col gap-2 rounded-xl border border-border px-3 py-3 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-sm font-semibold text-ink truncate">
                    {invoice.invoice_number || docType}
                  </p>
                  <span
                    className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium border ${
                      STATUS_STYLES[status] || STATUS_STYLES.draft
                    }`}
                  >
                    {status.replace(/_/g, ' ')}
                  </span>
                  <span className="text-[11px] font-medium text-ink-tertiary uppercase tracking-wide">
                    {docType}
                  </span>
                </div>
                <p className="mt-1 text-xs text-ink-secondary">
                  {money}
                  <span className="text-ink-tertiary"> · </span>
                  {formatDate(invoice.invoice_date || invoice.due_date)}
                  {status === 'draft' && variant === 'client' ? (
                    <span className="ml-1.5 text-ink-tertiary font-medium">
                      · Waiting for vendor to send
                    </span>
                  ) : null}
                  {showActionNeeded ? (
                    <span className="ml-1.5 text-amber-700 font-medium">· Action needed</span>
                  ) : null}
                </p>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                {variant === 'vendor' ? (
                  <Link
                    to={`/vendor/invoices/${invoice.id}`}
                    className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-border px-3 py-2 text-xs font-medium text-ink hover:bg-surface-muted"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    View
                  </Link>
                ) : canDownload ? (
                  <button
                    type="button"
                    disabled={isDownloading}
                    onClick={() => onDownload?.(invoice)}
                    className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-ink px-3 py-2 text-xs font-medium text-white hover:bg-black disabled:opacity-50"
                  >
                    {isDownloading ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Download className="w-3.5 h-3.5" />
                    )}
                    {isDownloading ? 'Downloading…' : 'Download'}
                  </button>
                ) : (
                  <span className="inline-flex items-center rounded-lg border border-border px-3 py-2 text-xs font-medium text-ink-tertiary">
                    Not available yet
                  </span>
                )}
              </div>
            </li>
          );
        })}
      </ul>
      )}
    </section>
  );
};

export default BookingLinkedInvoices;
